//Node Modules
import React, { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useSetRecoilState, useRecoilValue } from "recoil";
import _ from "lodash";

//GraphQL
import { API, graphqlOperation } from "aws-amplify";
import { getDevice } from "../../../graphql/queries";
import { updateDevice } from "../../../graphql/mutations";
import {
	listAlarmGroupsInputIds,
	listAlarmGroupsOutputIds,
	getDeviceFlowstops,
	getDeviceSensors,
} from "../../../graphql/queries-custom";
import { onUpdateDeviceDetails } from "../../../graphql/subscriptions-custom";

//BinaryForge Components
import { PageHeader } from "../../../components/layout";
import { Note } from "../../../components/helpers";
import {
	ConfigUpdateDialog,
	AttributesUpdateDialog,
	DeviceAlarmGroupDialog,
	DeleteDisabledDialog,
} from "../../../components/device";
import {
	DeviceDetailsOverviewSection,
	DeviceDetailsLiveStateSection,
	DeviceDetailsConfigSection,
	DeviceDetailsDiagnosticsSection,
	DeviceDetailsMetaSection,
} from "../../../components/device/details";

//3rd Party Components
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Accordion, AccordionTab } from "primereact/accordion";
import { InputSwitch } from "primereact/inputswitch";

//Atoms
import { dialogAtomFamily, loggedInUserAtom } from "../../../atoms";
import { selectedUserSchemeAtom } from "../../../atoms/user";
import { deviceSensorAlarmListAtom } from "../../../atoms/alarm";
import { deviceCommsStateAtom } from "../../../atoms/devices";
import { useApiRequest } from "../../../helpers/Api";

//Helpers
import { getRelayString, useGetFullLocation, canControlValve } from "../../../helpers/Device";
import { useSensorActuatorLabels } from "../../../config/device";
import { safeJsonParse } from "../../../helpers/Json";
import AssignedTenantDialog from "../../../components/device/AssignedTenantDialog";
import { buildDeviceDetailsViewModel } from "../../../components/device/details/deviceDetailsViewModel";

//Other
import { nav } from "../../../config/navigation";

//Overview and Live State now live in an always-open card; the accordion only holds
//the secondary Config/Diagnostics sections, collapsed by default to keep the page short.
const getDefaultAccordionSections = () => [];

const DeviceDetails = () => {
	const { t } = useTranslation();
	const apiRequest = useApiRequest();
	const { deviceId } = useLoaderData();
	const getFullLocation = useGetFullLocation();
	const navigate = useNavigate();

	// Recoil
	const loggedInUser = useRecoilValue(loggedInUserAtom);
	const selectedScheme = useRecoilValue(selectedUserSchemeAtom);
	const activeAlarms = useRecoilValue(deviceSensorAlarmListAtom);
	const commsState = useRecoilValue(deviceCommsStateAtom);
	const setLoading = useSetRecoilState(dialogAtomFamily("loadingDialog"));
	const setShowConfigUpdateDialog = useSetRecoilState(dialogAtomFamily("configUpdateDialog"));
	const setShowAttributesUpdateDialog = useSetRecoilState(dialogAtomFamily("deviceAttributesUpdateDialog"));
	const setShowDeviceAlarmGroupDialog = useSetRecoilState(dialogAtomFamily("deviceAlarmGroupDialog"));
	const setShowAssignedTenantDialog = useSetRecoilState(dialogAtomFamily("assignedTenantDialog"));
	const setShowDeleteDisabledDialog = useSetRecoilState(dialogAtomFamily("deleteDisabledDialog"));

	//Local State
	const [deviceInfo, setDeviceInfo] = useState(null);
	const [flowstops, setFlowstops] = useState([]);
	const [sensors, setSensors] = useState([]);
	const [reportedState, setReportedState] = useState(null);
	const [modbusState, setModbusState] = useState(null);
	const [desiredState, setDesiredState] = useState(null);
	const [desiredStateInfo, setDesiredStateInfo] = useState(null);
	const [inAlarm, setInAlarm] = useState(null);
	const [selectedAlarmGroups, setSelectedAlarmGroups] = useState({ type: "input", data: [] });
	const [deviceTsFormat, setDeviceTsFormat] = useState(true);
	const [activeAccordionSections, setActiveAccordionSections] = useState(getDefaultAccordionSections(false));
	const [deleteDisabledProps, setDeleteDisabledProps] = useState();

	//Temporary toggle: old fw only publishes flow + config once on connect, so they go stale.
	//Default hides those fields; flip on to see live telemetry (new fw). Remove once demo fleet is updated.
	const [showLiveTelemetry, setShowLiveTelemetry] = useState(
		() => localStorage.getItem("andelDeviceLiveTelemetry") === "true",
	);
	const handleToggleLiveTelemetry = (value) => {
		setShowLiveTelemetry(value);
		localStorage.setItem("andelDeviceLiveTelemetry", String(value));
	};

	const isAdmin = selectedScheme?.role === "ADMINISTRATOR";
	const isTenant = selectedScheme?.role === "TENANT";
	const canControlRelay = canControlValve(deviceInfo, reportedState);

	//On Page Load
	useEffect(() => {
		handleGetDevice();
		handleGetFlowstops();
		handleGetSensors();
		const updateDeviceSubscription = subscribeDeviceState();

		return () => {
			updateDeviceSubscription.unsubscribe();
		};
		// Re-run on deviceId change so navigating between linked devices (e.g. a Trigger pill)
		// refetches and re-subscribes instead of leaving the previous device's data on screen.
	}, [deviceId]);

	useEffect(() => {
		setActiveAccordionSections(getDefaultAccordionSections(isAdmin));
	}, [isAdmin]);

	//Get the initial device data
	const handleGetDevice = async () => {
		setLoading({ visible: true, message: t("device.loading") });
		const {
			data: { getDevice: deviceData },
		} = await API.graphql(graphqlOperation(getDevice, { id: deviceId }));

		processDeviceData(deviceData);
		setLoading({ visible: false, message: "" });
	};

	//Flowstops linked to this device via its input alarm groups - the outputs that act when it alarms.
	//Fetched once on load; each FlowstopItem subscribes for its own live valve state.
	const handleGetFlowstops = async () => {
		try {
			const {
				data: { getDevice: deviceData },
			} = await API.graphql(graphqlOperation(getDeviceFlowstops, { id: deviceId }));

			const linkedOutputs = (deviceData?.alarmGroupsInput?.items ?? []).flatMap(
				(input) =>
					(input?.alarmGroup?.output?.items ?? [])
						.map((output) => output?.device)
						.filter(Boolean),
			);
			setFlowstops(_.uniqBy(linkedOutputs, "id"));
		} catch (err) {
			console.warn("Get Flowstops Error ::", err);
		}
	};

	//Sensors linked to this device via its output alarm groups - the inputs that trigger it (inverse of flowstops).
	const handleGetSensors = async () => {
		try {
			const {
				data: { getDevice: deviceData },
			} = await API.graphql(graphqlOperation(getDeviceSensors, { id: deviceId }));

			const linkedInputs = (deviceData?.alarmGroupsOutput?.items ?? []).flatMap((output) =>
				(output?.alarmGroup?.input?.items ?? []).map((input) => input?.device).filter(Boolean),
			);
			setSensors(_.uniqBy(linkedInputs, "id"));
		} catch (err) {
			console.warn("Get Sensors Error ::", err);
		}
	};

	const subscribeDeviceState = () => {
		const sub = API.graphql(
			graphqlOperation(onUpdateDeviceDetails, {
				id: deviceId,
			}),
		).subscribe({
			next: ({ provider, value }) => {
				// console.log("Sub Update ::", provider, value);
				processDeviceData(value.data.onUpdateDeviceById);
			},
			error: (error) => console.warn(error),
		});

		return sub;
	};

	const processDeviceData = async (deviceData) => {
		const reportedStateObj = safeJsonParse(deviceData?.reported_state, {}, "reported_state");
		const modbusStateRaw = safeJsonParse(deviceData?.modbus_state, [], "modbus_state");
		const modbusStateObj = Array.isArray(modbusStateRaw) ? modbusStateRaw[0] || {} : modbusStateRaw || {};
		const desiredStateObj = safeJsonParse(deviceData?.desired_state, {}, "desired_state");
		const desiredStateInfoObj = safeJsonParse(deviceData?.desired_state_info, {}, "desired_state_info");

		//getFullLocation walks the zone tree with a GraphQL call per level. A failure there
		//used to abort processDeviceData and leave the page blank, so the device now renders
		//without its location instead.
		let locationBreadcrumb = [];
		let tenants = [];
		try {
			const location = await getFullLocation(deviceData.zone);
			locationBreadcrumb = location.locationBreadcrumb || [];
			tenants = location.tenants || [];
		} catch (error) {
			console.warn("Failed to resolve device location", error);
		}

		const alarmGroups = {};
		alarmGroups.input = deviceData.alarmGroupsInput.items.map((input) => {
			return {
				name: input.alarmGroup.name,
				id: input.alarmGroup.id,
				otherDevices: input.alarmGroup.input.items.length,
			};
		});
		alarmGroups.output = deviceData.alarmGroupsOutput.items.map((output) => {
			return {
				name: output.alarmGroup.name,
				id: output.alarmGroup.id,
				otherDevices: output.alarmGroup.output.items.length,
			};
		});

		const leakDistance = modbusStateObj.registers?.find((register) => register.name === "leakDistance")?.value;
		if (leakDistance === 65535 || leakDistance === 0) modbusStateObj.leakDistance = "No Leak";
		else if (leakDistance < 65535) modbusStateObj.leakDistance = leakDistance / 10;
		else modbusStateObj.leakDistance = null;

		setDeviceInfo({
			name: deviceData.name,
			createdAt: deviceData.createdAt,
			updatedAt: deviceData.updatedAt,
			inputDevice: deviceData.inputDevice,
			outputDevice: deviceData.outputDevice,
			alarmGroups: alarmGroups,
			zone: deviceData.zone,
			zoneBreadcrumb: locationBreadcrumb,
			tenants: tenants,
			tenantsCount: tenants.reduce((acc, tenant) => acc + tenant.tenantIds.length, 0),
			liveEventCount: deviceData.liveEvent?.items?.length,
			liveEventIds: deviceData.liveEvent?.items?.map((liveEvent) => liveEvent.id),
			archiveEventCount: deviceData.archiveEvent?.items?.length,
		});
		setReportedState(reportedStateObj);
		setModbusState(modbusStateObj);
		setDesiredState(desiredStateObj);
		setDesiredStateInfo(desiredStateInfoObj);
		setInAlarm(deviceData.alert?.sensor_state > 0);
	};

	const updateDeviceInfo = async (deviceInfo) => {
		setDeviceInfo(deviceInfo);
	};

	const handleShowAlarmGroupDialog = (type) => {
		setSelectedAlarmGroups({
			type: type,
			data: type === "input" ? deviceInfo.alarmGroups.input : deviceInfo.alarmGroups.output,
		});
		setShowDeviceAlarmGroupDialog(true);
	};

	const confirmToggleRelay = () => {
		if (isTenant || !canControlRelay) return;
		confirmDialog({
			header: t("device.dialog.toggleRelay.header"),
			message: (
				<>
					<p>
						{
							<Trans
								i18nKey="device.dialog.toggleRelay.message"
								values={{
									from: getRelayString(Boolean(reportedState?.relay?.status)),
									to: getRelayString(Boolean(!reportedState?.relay?.status)),
									device: deviceId,
								}}
								components={{ b: <b /> }}
							/>
						}
					</p>
				</>
			),
			acceptLabel: t("device.dialog.toggleRelay.accept"),
			rejectLabel: t("common.action.cancel"),
			acceptIcon: "pi pi-check",
			acceptClassName: "success feature",
			rejectClassName: "",
			accept: () => doToggleRelay(),
			reject: () => {}, //Do Nothing,
		});
	};

	const doToggleRelay = async () => {
		if (isTenant || !canControlRelay) return;
		const desiredState = {
			thingName: deviceId,
			desiredState: {
				relay: {
					status: Boolean(!reportedState?.relay?.status),
				},
			},
		};

		await apiRequest("post", "/shadow/desired/update", desiredState, t("device.dialog.toggleRelay.loading"));

		const updatedDesiredStateInfo = {
			...desiredStateInfo,
			relay: {
				value: Boolean(!reportedState?.relay?.status),
				createdAt: new Date().getTime(),
				createdBy: loggedInUser.username,
			},
		};

		await API.graphql(
			graphqlOperation(updateDevice, {
				input: {
					id: deviceId,
					desired_state_info: JSON.stringify(updatedDesiredStateInfo),
				},
			}),
		);
	};

	const confirmDeleteDevice = () => {
		if (isTenant) return;
		const lf = new Intl.ListFormat("en");
		const lastInputs = [];
		const lastOutputs = [];
		if (deviceInfo.alarmGroups.input.length > 0) {
			for (const input of deviceInfo.alarmGroups.input) {
				if (input.otherDevices <= 1) lastInputs.push(input.name);
			}
		}
		if (deviceInfo.alarmGroups.output.length > 0) {
			for (const output of deviceInfo.alarmGroups.output) {
				if (output.otherDevices <= 1) lastOutputs.push(output.name);
			}
		}

		if (lastInputs.length > 0) {
			setDeleteDisabledProps({ type: "INPUT", groups: lf.format(lastInputs) });
			setShowDeleteDisabledDialog(true);
		} else if (lastOutputs.length > 0) {
			setDeleteDisabledProps({ type: "OUTPUT", groups: lf.format(lastOutputs) });
			setShowDeleteDisabledDialog(true);
		} else {
			confirmDialog({
				header: t("device.dialog.delete.header"),
				message: (
					<div className="flexVert gapRow-medium">
						<p>
							{
								<Trans
									i18nKey="device.dialog.delete.message"
									values={{
										deviceId: deviceId,
										deviceName: deviceInfo.name,
									}}
									components={{ b: <b /> }}
								/>
							}
						</p>
						{inAlarm && (
							<Note
								messageKey={t("device.dialog.delete.inAlarmWarn")}
								icon="pi pi-exclamation-triangle fontColour-warn"
							/>
						)}
						{deviceInfo.alarmGroups.input.length > 0 && (
							<Note
								messageKey={t("device.dialog.delete.alarmGroupWarn", {
									count: deviceInfo.alarmGroups.input.length,
									type: t(
										useSensorActuatorLabels
											? "device.dialog.attributes.form.sensorType.label"
											: "device.dialog.attributes.form.inputDevice.label",
									),
								})}
								icon="pi pi-exclamation-triangle fontColour-warn"
							/>
						)}
						{deviceInfo.alarmGroups.output.length > 0 && (
							<Note
								messageKey={t("device.dialog.delete.alarmGroupWarn", {
									count: deviceInfo.alarmGroups.output.length,
									type: t(
										useSensorActuatorLabels
											? "device.dialog.attributes.form.actuatorType.label"
											: "device.dialog.attributes.form.outputDevice.label",
									),
								})}
								icon="pi pi-exclamation-triangle fontColour-warn"
							/>
						)}
					</div>
				),
				acceptLabel: t("common.action.delete"),
				rejectLabel: t("common.action.cancel"),
				acceptIcon: "pi pi-trash",
				acceptClassName: "error feature",
				rejectClassName: "",
				accept: () => doDeleteDevice(),
				reject: () => {}, //Do Nothing,
			});
		}
	};

	const doDeleteDevice = async () => {
		if (isTenant) return;
		try {
			setLoading({ visible: true, message: "Deleting Device" });

			const {
				data: {
					listAlarmGroupsInputs: { items: alarmInputs },
				},
			} = await API.graphql(
				graphqlOperation(listAlarmGroupsInputIds, { filter: { deviceID: { eq: deviceId } } }),
			);

			const {
				data: {
					listAlarmGroupsOutputs: { items: alarmOutputs },
				},
			} = await API.graphql(
				graphqlOperation(listAlarmGroupsOutputIds, { filter: { deviceID: { eq: deviceId } } }),
			);

			const liveEvents = deviceInfo.liveEventIds;

			await apiRequest("del", `/device/${deviceId}`, { data: { alarmInputs, alarmOutputs, liveEvents } }, null);

			navigate(`${nav.device.base}/${nav.device.management}`);
		} catch (err) {
			console.error("Delete Device Error ::", err);
		} finally {
			setLoading({ visible: false });
		}
	};

	const viewModel = buildDeviceDetailsViewModel({
		deviceId,
		deviceInfo,
		reportedState,
		modbusState,
		desiredState,
		desiredStateInfo,
		isAdmin,
		commsEvent: commsState?.[deviceId] ?? null,
	});

	return (
		<>
			<PageHeader
				title={t("device.pageTitle", { deviceId: deviceInfo?.name })}
				subtitle={t("device.pageSubtitle")}
				enableBack
			/>
			<main className="appMain">
				<div className="appMain-content">
					{!isTenant && (
						<div className="flex jContent-end gap-small">
							{/* <Button onClick={() => doClearDesiredState()} className="error">Clear Desired</Button> */}
							<Button
								onClick={() => confirmDeleteDevice()}
								visible={process.env.REACT_APP_ENABLE_DEVICE_DELETE === "true"}
								className="error"
							>
								{t("device.actions.delete")}
							</Button>
							{canControlRelay && (
								<Button onClick={() => confirmToggleRelay()}>{t("device.actions.valveControl")}</Button>
							)}
							{isAdmin && (
								<Button onClick={() => setShowConfigUpdateDialog(true)}>
									{t("device.actions.config")}
								</Button>
							)}
							<Button onClick={() => setShowAttributesUpdateDialog(true)}>
								{t("device.actions.attributes")}
							</Button>
						</div>
					)}

					{/* Live alarm atom only - mixing in the fetched alert snapshot causes stuck banners (see #49) */}
					{activeAlarms?.some((alarm) => alarm?.device?.id === deviceId) ? (
						<div className="deviceStatusBar deviceStatusBar-alarm flex aItems-center gap-xsmall">
							<i className="pi pi-exclamation-triangle" />
							<span>{t("device.state.inAlarmBanner")}</span>
						</div>
					) : (
						<div className="deviceStatusBar deviceStatusBar-clear flex aItems-center gap-xsmall">
							<i className="pi pi-check-circle" />
							<span>{t("device.state.clearBanner")}</span>
						</div>
					)}

					{viewModel && (
						<>
							<div
								className={`card flexVert gap-small ${
									viewModel.offlineState?.isOffline ? "liveStateCard-offline" : ""
								}`}
							>
								<DeviceDetailsOverviewSection
									t={t}
									viewModel={viewModel}
									embedded
									onShowAssignedTenantDialog={() => setShowAssignedTenantDialog(true)}
								/>
								<DeviceDetailsLiveStateSection
									t={t}
									viewModel={viewModel}
									flowstops={flowstops}
									sensors={sensors}
									showLiveTelemetry={showLiveTelemetry}
									embedded
								/>
							</div>

							<Accordion
								multiple
								activeIndex={activeAccordionSections}
								onTabChange={(e) => setActiveAccordionSections(Array.isArray(e.index) ? e.index : [])}
							>
								<AccordionTab header="Details">
									<DeviceDetailsMetaSection
										t={t}
										viewModel={viewModel}
										onShowAlarmGroupDialog={handleShowAlarmGroupDialog}
									/>
								</AccordionTab>
								{isAdmin && showLiveTelemetry && (
									<AccordionTab header="Configuration">
										<DeviceDetailsConfigSection t={t} viewModel={viewModel} />
									</AccordionTab>
								)}
								<AccordionTab header="Diagnostics">
									<DeviceDetailsDiagnosticsSection
										t={t}
										viewModel={viewModel}
										deviceTsFormat={deviceTsFormat}
										onToggleDeviceTimestampFormat={() =>
											setDeviceTsFormat((currentValue) => !currentValue)
										}
									/>
								</AccordionTab>
							</Accordion>

							<div className="actions flex jContent-end aItems-center gap-xsmall">
								<label htmlFor="liveTelemetryToggle" className="fontSize-small fontColour-light">
									{t("device.liveTelemetryToggle")}
								</label>
								<InputSwitch
									inputId="liveTelemetryToggle"
									checked={showLiveTelemetry}
									onChange={(e) => handleToggleLiveTelemetry(e.value)}
								/>
							</div>
						</>
					)}
					{/* <pre>{JSON.stringify(device, null, 2)}</pre>
						<pre>{JSON.stringify(reportedState, null, 2)}</pre>
						<pre>{JSON.stringify(desiredState, null, 2)}</pre> */}
				</div>
			</main>

			{isAdmin && reportedState && (
				<ConfigUpdateDialog
					deviceId={deviceId}
					deviceData={reportedState}
					desiredStateInfo={desiredStateInfo}
				/>
			)}
			{deleteDisabledProps && (
				<DeleteDisabledDialog type={deleteDisabledProps.type} groups={deleteDisabledProps.groups} />
			)}
			{deviceInfo && (
				<AttributesUpdateDialog
					deviceId={deviceId}
					deviceData={deviceInfo}
					updateDeviceData={updateDeviceInfo}
				/>
			)}
			{deviceInfo && (
				<DeviceAlarmGroupDialog type={selectedAlarmGroups.type} alarmGroups={selectedAlarmGroups.data} />
			)}
			{deviceInfo?.tenants?.length > 0 && <AssignedTenantDialog tenants={deviceInfo.tenants} />}
		</>
	);
};

export default DeviceDetails;
